import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SiteShell } from "@/components/layout/SiteShell";
import { Button } from "@/components/ui/button";
import { getEnSearchWeekly, getEnClickWeekly } from "@/lib/reports.functions";
import { EN_SNIPPET_VARIANTS, enSnippetVariant } from "@/lib/abTest";

export const Route = createFileRoute("/admin-en-rapport")({
  head: () => ({
    meta: [
      { title: "Rapport /en/ — Tailor's Barber" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EnReportPage,
});

function download(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const PW_KEY = "tb_admin_pw";

function PasswordGate({ onUnlock }: { onUnlock: (pw: string) => void }) {
  const [value, setValue] = useState("");
  return (
    <SiteShell>
      <div className="mx-auto max-w-sm px-4 py-20">
        <h1 className="text-2xl font-semibold">Intern rapport</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Den här sidan är låst. Ange adminlösenordet för att se datan.
        </p>
        <form
          className="mt-6 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (value) onUnlock(value);
          }}
        >
          <input
            type="password"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Lösenord"
            aria-label="Adminlösenord"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <Button type="submit" className="w-full">
            Logga in
          </Button>
        </form>
      </div>
    </SiteShell>
  );
}

function EnReportPage() {
  const searchWeekly = useServerFn(getEnSearchWeekly);
  const clickWeekly = useServerFn(getEnClickWeekly);
  const [weeks, setWeeks] = useState(8);
  const [password, setPassword] = useState<string | null>(() =>
    typeof window === "undefined" ? null : sessionStorage.getItem(PW_KEY),
  );

  const unlock = (pw: string) => {
    sessionStorage.setItem(PW_KEY, pw);
    setPassword(pw);
  };

  const lock = () => {
    sessionStorage.removeItem(PW_KEY);
    setPassword(null);
  };

  const search = useQuery({
    queryKey: ["en-gsc-weekly", weeks, password],
    enabled: !!password,
    retry: false,
    queryFn: () => searchWeekly({ data: { weeks, query: "barber near me", password: password! } }),
  });

  const clicks = useQuery({
    queryKey: ["en-clicks-weekly", weeks, password],
    enabled: !!password,
    retry: false,
    queryFn: () => clickWeekly({ data: { weeks, password: password! } }),
  });


  const unauthorized =
    (search.data && "unauthorized" in search.data && search.data.unauthorized) ||
    (clicks.data && !Array.isArray(clicks.data) && clicks.data.unauthorized) ||
    false;

  const searchData =
    search.data && !("unauthorized" in search.data && search.data.unauthorized)
      ? (search.data as { siteUrl: string; rows: import("@/lib/reports.functions").EnGscWeek[] })
      : null;
  const clicksData = Array.isArray(clicks.data) ? clicks.data : null;

  const chartData = useMemo(
    () =>
      (searchData?.rows ?? []).map((r) => ({
        week: r.weekStart.slice(5),
        ctr: Number((r.ctr * 100).toFixed(2)),
        position: Number(r.position.toFixed(1)),
        impressions: r.impressions,
        clicks: r.clicks,
      })),
    [searchData],
  );

  const searchCsv = () => {
    const rows = searchData?.rows ?? [];
    return [
      "week_start,week_end,variant,clicks,impressions,ctr_percent,avg_position",
      ...rows.map((r) =>
        [
          r.weekStart,
          r.weekEnd,
          r.variant,
          r.clicks,
          r.impressions,
          (r.ctr * 100).toFixed(2),
          r.position.toFixed(2),
        ].join(","),
      ),
    ].join("\n");
  };

  const clicksCsv = () => {
    const rows = clicksData ?? [];
    return [
      "week_start,salon,internal_link_clicks,bokadirekt_cta_clicks",
      ...rows.map((r) =>
        [r.weekStart, r.salon, r.internalClicks, r.bokadirektClicks].join(","),
      ),
    ].join("\n");
  };


  const live = enSnippetVariant();

  if (!password) return <PasswordGate onUnlock={unlock} />;

  if (unauthorized) {

    return (
      <SiteShell>
        <div className="mx-auto max-w-sm px-4 py-20">
          <p className="text-sm text-red-600">Fel lösenord.</p>
          <Button className="mt-4 w-full" onClick={lock}>
            Försök igen
          </Button>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-semibold">Rapport — /en/ &amp; ”barber near me”</h1>
          <Button size="sm" variant="outline" onClick={lock}>
            Logga ut
          </Button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Veckovis data från Search Console (Mån–Sön, 3 dagars datafördröjning) plus
          förstapartsklick på interna salongslänkar och BokaDirekt-CTAs på /en/.
        </p>



        <div className="mt-6 flex flex-wrap items-center gap-3">
          {[4, 8, 12, 26].map((w) => (
            <Button key={w} size="sm" variant={w === weeks ? "default" : "outline"} onClick={() => setWeeks(w)}>
              {w} veckor
            </Button>
          ))}
        </div>

        <section className="mt-10">
          <h2 className="text-xl font-semibold">Search Console — trend</h2>
          {search.isLoading && <p className="mt-3 text-sm text-muted-foreground">Hämtar…</p>}
          {search.isError && (
            <p className="mt-3 text-sm text-red-600">Fel: {(search.error as Error).message}</p>
          )}
          {searchData && (
            <>
              <div className="mt-4 h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" reversed />
                    <Tooltip />
                    <Line yAxisId="left" type="monotone" dataKey="ctr" name="CTR %" stroke="#b08d57" />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="position"
                      name="Position"
                      stroke="#1f2937"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2">Vecka</th>
                      <th className="px-4 py-2">Variant</th>
                      <th className="px-4 py-2">Klick</th>
                      <th className="px-4 py-2">Visningar</th>
                      <th className="px-4 py-2">CTR</th>
                      <th className="px-4 py-2">Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchData.rows.map((r) => (
                      <tr key={r.weekStart} className="border-t border-border">
                        <td className="px-4 py-2">
                          {r.weekStart} – {r.weekEnd}
                        </td>
                        <td className="px-4 py-2">{r.variant}</td>
                        <td className="px-4 py-2">{r.clicks}</td>
                        <td className="px-4 py-2">{r.impressions}</td>
                        <td className="px-4 py-2">{(r.ctr * 100).toFixed(2)} %</td>
                        <td className="px-4 py-2">{r.position.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4">
                <Button size="sm" onClick={() => download("en-barber-near-me-weekly.csv", searchCsv())}>
                  Exportera CSV
                </Button>
              </div>
            </>
          )}
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-semibold">Interna länkar &amp; BokaDirekt-CTAs på /en/</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Räknas direkt i vår egen databas, så flödet syns innan Search Console-CTR rör sig.
          </p>
          {clicks.isLoading && <p className="mt-3 text-sm text-muted-foreground">Hämtar…</p>}
          {clicks.isError && (
            <p className="mt-3 text-sm text-red-600">Fel: {(clicks.error as Error).message}</p>
          )}
          {clicksData && (
            <>
              <div className="mt-4 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2">Vecka</th>
                      <th className="px-4 py-2">Salong</th>
                      <th className="px-4 py-2">Interna länkklick</th>
                      <th className="px-4 py-2">BokaDirekt-klick</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clicksData.length === 0 && (
                      <tr>
                        <td className="px-4 py-3 text-muted-foreground" colSpan={4}>
                          Inga klick registrerade ännu.
                        </td>
                      </tr>
                    )}
                    {clicksData.map((r) => (
                      <tr key={`${r.weekStart}-${r.salon}`} className="border-t border-border">
                        <td className="px-4 py-2">{r.weekStart}</td>
                        <td className="px-4 py-2">{r.salon}</td>
                        <td className="px-4 py-2">{r.internalClicks}</td>
                        <td className="px-4 py-2">{r.bokadirektClicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <Button size="sm" onClick={() => download("en-link-clicks-weekly.csv", clicksCsv())}>
                  Exportera CSV
                </Button>
              </div>
            </>
          )}
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-semibold">Snippet-test</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Google visar bara en titel per URL, så testet körs sekventiellt: variant växlar varje
            ISO-vecka och varje vecka i tabellen ovan är märkt med den variant som var live. Just nu
            är <strong>variant {live.id}</strong> live.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {EN_SNIPPET_VARIANTS.map((v) => (
              <div key={v.id} className="rounded-lg border border-border p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Variant {v.id}
                  {v.id === live.id ? " (live)" : ""}
                </div>
                <p className="mt-2 font-medium">{v.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{v.description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </SiteShell>
  );
}
